// client/src/search/smartSearch.js
import { expandToken } from "./synonyms";
import { norm, tokenize } from "./normalize";
import { extractInvoiceDigits, shouldUseInvoiceDigitMatch } from "./invoiceKeys";

export { norm, tokenize };
export { deUmlaut } from "./normalize";

/* ===== Dates ===== */
export const toDate = (s) => {
  if (!s) return null;
  const str = String(s);
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(str) ? `${str}T12:00` : str;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const fmtISODate = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    : "";

export const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export const weekOf = (d) => {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
};

export const MONTHS = [
  "januar","februar","maerz","april","mai","juni","juli","august","september","oktober","november","dezember",
  "jan","feb","mrz","apr","mai","jun","jul","aug","sep","okt","nov","dez",
];

// “harte” Date-Tokens: sollen NICHT als Text-Substring matchen
function isHardDateToken(t) {
  return (
    /^kw\d{1,2}$/.test(t) ||
    /^\d{4}-\d{2}-\d{2}$/.test(t) ||
    /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(t) ||
    /^\d{1,2}\.\d{1,2}\.$/.test(t)
  );
}

export function tokenMatchesDates(token, dateList /* Date[] */, now = new Date()) {
  if (!Array.isArray(dateList) || dateList.length === 0) return false;
  const t = token;

  if (t === "heute") return dateList.some((d) => sameDay(d, now));
  if (t === "morgen") return dateList.some((d) => sameDay(d, addDays(now, 1)));
  if (t === "gestern") return dateList.some((d) => sameDay(d, addDays(now, -1)));

  // NUR als Phrase – nicht "woche" alleine!
  if (t === "diese_woche") {
    const w = weekOf(now), y = now.getFullYear();
    return dateList.some((d) => weekOf(d) === w && d.getFullYear() === y);
  }
  if (t === "naechste_woche") {
    const next = addDays(now, 7);
    const w = weekOf(next), y = next.getFullYear();
    return dateList.some((d) => weekOf(d) === w && d.getFullYear() === y);
  }
  if (t === "letzte_woche") {
    const prev = addDays(now, -7);
    const w = weekOf(prev), y = prev.getFullYear();
    return dateList.some((d) => weekOf(d) === w && d.getFullYear() === y);
  }

  const m = t.match(/^kw(\d{1,2})$/);
  if (m) {
    const want = Number(m[1]);
    return dateList.some((d) => weekOf(d) === want);
  }

  if (MONTHS.includes(t)) {
    const idx = MONTHS.indexOf(t) % 12;
    return dateList.some((d) => d.getMonth() === idx);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return dateList.some((d) => fmtISODate(d) === t);

  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(t)) {
    const [dd, mm, yyyy] = t.split(".");
    const want = `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
    return dateList.some((d) => fmtISODate(d) === want);
  }

  if (/^\d{1,2}\.\d{1,2}\.$/.test(t)) {
    const [dd, mm] = t.split(".").filter(Boolean);
    return dateList.some(
      (d) => d.getDate() === Number(dd) && d.getMonth() + 1 === Number(mm)
    );
  }

  if (/^\d{4}$/.test(t)) return dateList.some((d) => d.getFullYear() === Number(t));
  return false;
}

/* ===== Fuzzy ===== */
export const oneOffEqual = (a, b) => {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (Math.max(la, lb) < 6) return false; // strenger => weniger “global”
  let i = 0, j = 0, diff = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++diff > 1) return false;
    if (la > lb) i++;
    else if (lb > la) j++;
    else { i++; j++; }
  }
  diff += (la - i) + (lb - j);
  return diff <= 1;
};

/* ===== Kern-Matcher (präziser) ===== */
function matchesTextVariant(v, idx) {
  if (!v) return false;
  const L = v.length;

  // kurze Tokens nur als “ganzes Wort”
  if (L <= 3) return idx.words.has(v);

  // ab 4: Wort-exakt oder substring
  if (idx.words.has(v)) return true;
  return idx.keys.some((k) => k.includes(v));
}

export function rowMatches(tokens, idx) {
  if (!tokens.length) return true;
  const now = new Date();

  for (const t of tokens) {
    let hit = false;

    // 1) Date first
    if (idx?.dates?.length) hit = tokenMatchesDates(t, idx.dates, now);

    // 2) harte Date-Tokens dürfen nicht als Text matchen
    if (!hit && isHardDateToken(t)) return false;

    // 3) Text/Synonyme
    if (!hit) {
      const variants = expandToken(t);
      for (const v of variants) {
        if (matchesTextVariant(v, idx)) { hit = true; break; }
      }

      // 4) Fuzzy nur auf Wörter
      if (!hit) {
        outer: for (const w of idx.wordArr) {
          for (const v of variants) {
            if (oneOffEqual(w, v)) { hit = true; break outer; }
          }
        }
      }

      // 5) Rechnungsnummer: Ziffern-Teilmatch (z. B. „502314“ findet „RG 5023145r“)
      if (!hit) {
        for (const v of variants) {
          if (!shouldUseInvoiceDigitMatch(v)) continue;
          const needle = extractInvoiceDigits(v);
          if (needle.length < 2) continue;
          for (const k of idx.keys) {
            const kd = extractInvoiceDigits(k);
            if (kd && kd.includes(needle)) {
              hit = true;
              break;
            }
          }
          if (hit) break;
        }
      }
    }

    if (!hit) return false;
  }

  return true;
}

/* ===== Indexer ===== */
const KEY_CACHE = new WeakMap();

export function buildRowIndex(row, ctx) {
  if (!row || typeof row !== "object") {
    return { keys: [], words: new Set(), wordArr: [], dates: [] };
  }

  const ctxKey = ctx?.cacheKey || ctx || "__default__";
  let byCtx = KEY_CACHE.get(row);
  if (byCtx?.has(ctxKey)) return byCtx.get(ctxKey);

  const normalizeKey = typeof ctx?.norm === "function" ? ctx.norm : norm;

  const rawKeys = (ctx?.buildKeys?.(row, ctx) || [])
    .filter(Boolean)
    .map(String);

  const finalKeys = rawKeys.map((k) => normalizeKey(k)); // <- WICHTIG: immer norm()

  const words = new Set();
  for (const k of finalKeys) {
    for (const w of String(k).split(" ")) if (w) words.add(w);
  }

  const dates = (ctx?.buildDates?.(row, ctx) || [])
    .map(toDate)
    .filter(Boolean);

  const out = { keys: finalKeys, words, wordArr: [...words], dates };

  if (!byCtx) {
    byCtx = new Map();
    KEY_CACHE.set(row, byCtx);
  }
  byCtx.set(ctxKey, out);
  return out;
}
